import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n';
import { CheckCircle2, Circle, X, Rocket } from 'lucide-react';

// 首日 checklist localStorage 键 (Conversations/Today 页写入, MyAssistants 读取共用)
export const CHAT_DONE_KEY = 'oz_checklist_chat_done';
export const TODAY_DONE_KEY = 'oz_checklist_today_done';
// dismiss 存时间戳: 未完成时中途关闭不永久 (24h 后/换模式后重新出现), 全完成才永久消失
export const CHECKLIST_DISMISS_KEY = 'oz_checklist_dismissed_at';
const DISMISS_HIDE_MS = 24 * 60 * 60 * 1000;

/**
 * 首日 checklist — 侧边栏轻量进度卡 (W27)。
 * 三步: 建助理 → 聊第一句 → 认识今日信息/仪表盘。全完成或用户关闭后消失。
 */
export default function FirstDayChecklist({ assistants = [], currentId = null, mode = 'business' }) {
  const t = useT();
  const navigate = useNavigate();
  const [chatDone, setChatDone] = useState(false);
  const [todayDone, setTodayDone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setChatDone(localStorage.getItem(CHAT_DONE_KEY) === '1');
      setTodayDone(localStorage.getItem(TODAY_DONE_KEY) === '1');
      const at = parseInt(localStorage.getItem(CHECKLIST_DISMISS_KEY) || '', 10);
      setDismissed(Number.isFinite(at) && Date.now() - at < DISMISS_HIDE_MS);
    } catch (_) {}
  }, []);

  // 侧边栏常驻不重挂 — 监听对话/今日页派发的事件即时刷新进度
  useEffect(() => {
    const onChat = () => setChatDone(true);
    const onToday = () => setTodayDone(true);
    window.addEventListener('oz:checklist-chat-done', onChat);
    window.addEventListener('oz:checklist-today-done', onToday);
    return () => {
      window.removeEventListener('oz:checklist-chat-done', onChat);
      window.removeEventListener('oz:checklist-today-done', onToday);
    };
  }, []);

  const hasAssistant = assistants.length > 0;
  const allDone = hasAssistant && chatDone && todayDone;
  if (dismissed || allDone || !hasAssistant) return null;

  const current = assistants.find(a => a.id === currentId) || assistants[0];
  const pub = current?.public_id || String(current?.id || '');
  const steps = [
    { key: 'assistant', done: hasAssistant, to: '/assistants' },
    { key: 'chat', done: chatDone, to: pub ? `/assistant/${pub}/conversations` : null },
    { key: 'today', done: todayDone, to: pub ? `/assistant/${pub}/dashboard` : null },
  ];
  const doneCount = steps.filter(s => s.done).length;

  const go = (to) => { if (to) navigate(to); };
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(CHECKLIST_DISMISS_KEY, String(Date.now())); } catch (_) {}
  };

  return (
    <div className="mx-3 mb-2 rounded-xl border p-3"
      style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
          <Rocket size={13} style={{ color: 'var(--brand)' }} />
          {t('checklist.title')}
        </span>
        <button onClick={dismiss} className="p-0.5 rounded transition-colors hover:bg-[var(--bg-hover)]"
          style={{ color: 'var(--text-muted)' }} aria-label={t('checklist.dismiss')}>
          <X size={12} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-2 mb-2.5">
        <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: 'var(--bg-hover)' }}>
          <div className="h-1 rounded-full transition-all duration-300"
            style={{ width: `${(doneCount / steps.length) * 100}%`, backgroundColor: 'var(--brand)' }} />
        </div>
        <span className="text-[10px] font-medium shrink-0" style={{ color: 'var(--text-muted)' }}>
          {doneCount}/{steps.length}
        </span>
      </div>

      <div className="space-y-1">
        {steps.map(s => {
          const label = s.key === 'today'
            ? (mode === 'family' ? t('checklist.step_today_family') : t('checklist.step_today_biz'))
            : t(`checklist.step_${s.key}`);
          return (
            <button key={s.key} onClick={() => go(s.to)}
              disabled={s.done}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[11px] transition-colors ${
                s.done ? '' : 'hover:bg-[var(--bg-hover)]'
              }`}
              style={{ color: s.done ? 'var(--text-muted)' : 'var(--text-secondary)', cursor: s.done ? 'default' : 'pointer' }}>
              {s.done
                ? <CheckCircle2 size={14} className="shrink-0" style={{ color: '#10b981' }} />
                : <Circle size={14} className="shrink-0" style={{ color: 'var(--text-muted)' }} />}
              <span className="truncate">{label}</span>
              {s.done && <span className="ml-auto text-[10px] shrink-0" style={{ color: '#10b981' }}>{t('checklist.done')}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
